import type {
  GeminiStatus,
  LanguageRecommendation,
  ManualModule,
  ManualQuery,
  PracticeManual,
  PracticeManualModule,
  PracticeMatch
} from "../types";
import {
  buildLanguageFallback,
  buildPracticeFallback,
  searchLanguageFormulas,
  searchPracticeModules,
  summarizeLanguageMatches,
  summarizePracticeModule
} from "../utils/manualEngine";

async function postJson<T>(url: string, payload: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error("API request failed");
  }

  return (await response.json()) as T;
}

export async function recommendLanguage(query: ManualQuery, modules: ManualModule[]): Promise<LanguageRecommendation> {
  const matches = searchLanguageFormulas(modules, query, 8);
  const fallback = buildLanguageFallback(query, matches);

  try {
    return await postJson<LanguageRecommendation>("/api/recommend-language", {
      query,
      matches: summarizeLanguageMatches(matches),
      fallback
    });
  } catch {
    return fallback;
  }
}

export function recommendPracticeModules(query: ManualQuery, modules: ManualModule[]): PracticeMatch[] {
  return searchPracticeModules(modules, query, 8);
}

export async function generatePracticeManual(
  query: ManualQuery,
  module: PracticeManualModule,
  allModules: ManualModule[]
): Promise<PracticeManual> {
  const matches = searchPracticeModules(allModules, { ...query, selectedModuleId: module.id }, 8);
  const fallback = buildPracticeFallback(query, module, matches);

  try {
    return await postJson<PracticeManual>("/api/generate-practice-manual", {
      query,
      module: summarizePracticeModule(module, matches),
      fallback
    });
  } catch {
    return fallback;
  }
}


export async function getGeminiStatus(): Promise<GeminiStatus> {
  try {
    return await postJson<GeminiStatus>("/api/gemini-status", {});
  } catch {
    return {
      configured: false,
      model: "unknown",
      status: "missing-key",
      checkedAt: new Date().toISOString()
    };
  }
}
